/**
 * Session Manager
 * Manages user sessions to prevent duplicate device info saves
 * Uses localStorage to track active sessions
 */

const SESSION_STORAGE_KEY = 'octopus_device_session';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

/**
 * Get current session from localStorage
 * @returns {Object|null} Session data or null if expired/not found
 */
export function getCurrentSession() {
  try {
    const sessionData = localStorage.getItem(SESSION_STORAGE_KEY);
    
    if (!sessionData) {
      return null;
    }
    
    const session = JSON.parse(sessionData);
    const now = Date.now();
    
    // Check if session has expired (30 minutes of inactivity)
    if (now - session.lastActivity > SESSION_TIMEOUT) {
      // Session expired, clear it
      clearSession();
      return null;
    }
    
    // Session is valid, update last activity
    updateSessionActivity();
    return session;
    
  } catch (error) {
    /* eslint-disable no-console */
    console.error('Error reading session:', error);
    /* eslint-enable no-console */
    return null;
  }
}

/**
 * Create a new session
 * @param {string} deviceDocId - Firebase document ID
 * @returns {Object} New session data
 */
export function createSession(deviceDocId) {
  const now = Date.now();
  const session = {
    deviceDocId,
    createdAt: now,
    lastActivity: now,
    pageLoads: 1
  };
  
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    return session;
  } catch (error) {
    /* eslint-disable no-console */
    console.error('Error creating session:', error);
    /* eslint-enable no-console */
    return session;
  }
}

/**
 * Update session activity timestamp
 */
export function updateSessionActivity() {
  try {
    const sessionData = localStorage.getItem(SESSION_STORAGE_KEY);
    
    if (!sessionData) {
      return;
    }
    
    const session = JSON.parse(sessionData);
    session.lastActivity = Date.now();
    session.pageLoads = (session.pageLoads || 0) + 1;
    
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    /* eslint-disable no-console */
    console.error('Error updating session activity:', error);
    /* eslint-enable no-console */
  }
}

/**
 * Clear current session
 */
export function clearSession() {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    /* eslint-disable no-console */
    console.error('Error clearing session:', error);
    /* eslint-enable no-console */
  }
}

/**
 * Get session statistics
 * @returns {Object} Session stats
 */
export function getSessionStats() {
  const session = getCurrentSession();
  
  if (!session) {
    return null;
  }
  
  const now = Date.now();
  const duration = now - session.createdAt;
  const lastActivityAgo = now - session.lastActivity;
  
  return {
    deviceDocId: session.deviceDocId,
    durationMs: duration,
    durationMinutes: Math.floor(duration / 60000),
    pageLoads: session.pageLoads,
    lastActivityAgo: Math.floor(lastActivityAgo / 1000), // seconds
    isActive: lastActivityAgo < SESSION_TIMEOUT
  };
}

export default {
  getCurrentSession,
  createSession,
  updateSessionActivity,
  clearSession,
  getSessionStats
};
